import { Handle, Position } from '@xyflow/react';

import { cn } from '@/lib/utils';

import { NODE_COLORS } from '../workflow-utils';

import type { ReactNode } from 'react';

export interface NodeSource {
  /** React Flow handle id — omit for a node with a single unnamed output. */
  id?: string;
  /** Vertical position, e.g. '30%'. Omit to centre. */
  top?: string;
  /** Overrides the node's accent, e.g. for condition's true/false. */
  handleClass?: string;
  /** Rendered beside the handle, outside the tile. */
  label?: string;
  labelClass?: string;
}

export interface NodeShellProps {
  /** Key into NODE_COLORS. */
  type: string;
  icon: ReactNode;
  label: string;
  /** One muted line under the name — the node's most useful config at a glance. */
  subtitle?: ReactNode;
  selected?: boolean;
  hasTarget?: boolean;
  /** Defaults to a single centred output. Pass [] for a terminal node. */
  sources?: NodeSource[];
  /** Start/End read better as circles. */
  round?: boolean;
}

const DEFAULT_SOURCES: NodeSource[] = [{}];

/**
 * The one card every workflow node renders.
 *
 * Deliberately n8n-shaped: a small square tile holding only the icon, with the
 * name and subtitle OUTSIDE and below it. Keeping text out of the tile is what
 * makes the graph compact — an earlier version put icon + title + subtitle +
 * chips inside one 190px-wide card and the canvas turned into a wall of
 * same-sized boxes.
 *
 * The tile is neutral and the type is carried by the icon's colour, so nodes
 * are told apart by their glyph rather than by a background wash.
 *
 * Node components pass content in and own no layout, so spacing, handles and
 * selection stay consistent across all of them.
 */
export const NodeShell = ({
  type,
  icon,
  label,
  subtitle,
  selected,
  hasTarget = true,
  sources = DEFAULT_SOURCES,
  round = false,
}: NodeShellProps) => {
  const colors = NODE_COLORS[type] ?? NODE_COLORS.start;
  const handleClass = '!h-2 !w-2 !border !border-white dark:!border-neutral-900';

  return (
    // Fixed width so a long name cannot stretch the node's bounding box and
    // drag the handles apart with it.
    <div className="flex w-[92px] flex-col items-center">
      <div
        className={cn(
          'bg-card relative flex h-14 w-14 items-center justify-center border shadow-sm transition-all',
          'hover:shadow-md',
          round ? 'rounded-full' : 'rounded-xl',
          selected ? 'border-primary ring-primary/30 ring-2' : 'border-border',
        )}
      >
        {hasTarget && (
          <Handle
            type="target"
            position={Position.Left}
            className={cn(handleClass, colors.handle)}
          />
        )}

        <div
          className={cn(
            'flex items-center justify-center rounded-lg',
            round ? 'h-7 w-7' : 'h-8 w-8',
            colors.tint,
            colors.icon,
          )}
        >
          {icon}
        </div>

        {sources.map((s, i) => (
          <Handle
            key={s.id ?? i}
            type="source"
            {...(s.id ? { id: s.id } : {})}
            position={Position.Right}
            {...(s.top ? { style: { top: s.top } } : {})}
            className={cn(handleClass, s.handleClass ?? colors.handle)}
          />
        ))}

        {/* Handle labels are absolute against the tile so they never affect the
            column's width. */}
        {sources
          .filter((s) => s.label)
          .map((s, i) => (
            <span
              key={`${s.id ?? i}-label`}
              className={cn(
                'pointer-events-none absolute left-full ml-2.5 text-[8px] leading-none font-semibold',
                s.labelClass,
              )}
              style={{ top: s.top ?? '50%', transform: 'translateY(-50%)' }}
            >
              {s.label}
            </span>
          ))}
      </div>

      <p className="text-foreground mt-1.5 max-w-full truncate text-[10px] leading-tight font-medium">
        {label}
      </p>
      {subtitle && (
        <p className="text-muted-foreground max-w-full truncate text-[9px] leading-tight">
          {subtitle}
        </p>
      )}
    </div>
  );
};
